import { useCallback, useEffect, useMemo, useRef } from 'react';

interface SaveChangesOptions {
  versionLabel?: string;
  immediate?: boolean;
}

interface UseVersioningReturnValue<T> {
  initialize: (versionLabel?: string) => void;
  saveChanges: (options?: SaveChangesOptions) => void;
  rollback: (rollbackAction: (state: T) => void, toVersion?: string) => void;
  reset: (versionLabel?: string) => void;
}

/**
 * Allows you to bookmark a given piece of state at a particular point in time and then
 * rollback to that state in the future.
 */
export default function useVersioning<T>(arg: T): UseVersioningReturnValue<T> {
  const bookmarks = useRef<Record<string, T>>({});
  const argRef = useRef<T>(arg);

  useEffect(() => {
    argRef.current = arg;
  });

  const handleSaveChanges = useCallback(
    ({ versionLabel = 'default', immediate = false }: SaveChangesOptions = {}) => {
      if (immediate) {
        bookmarks.current[versionLabel || 'default'] = argRef.current;
      } else {
        // This seems counter-intuitive but it's here for a reason. React may or may not batch updates/renders
        // depending on the situation setState is called from. If we make any changes just before calling save we
        // want to be sure the latest state is getting saved, which may not be the case if the updates were batched.
        // To get around this we schedule the update for the next event loop which will be after react has had a chance
        // to update.
        setTimeout(() => {
          bookmarks.current[versionLabel || 'default'] = argRef.current;
        }, 0);
      }
    },
    [],
  );

  const initialize = useCallback(
    (versionLabel?: string) => {
      if (!bookmarks.current[versionLabel || 'default']) {
        handleSaveChanges({ versionLabel, immediate: true });
      }
    },
    [handleSaveChanges],
  );

  const handleRollback = useCallback((rollbackAction: (state: T) => void, toVersion?: string) => {
    const bookmarkedState = bookmarks.current[toVersion || 'default'];

    rollbackAction(bookmarkedState);
  }, []);

  const reset = useCallback((versionLabel?: string) => {
    delete bookmarks.current[versionLabel || 'default'];
  }, []);

  const versioner = useMemo(
    () => ({ initialize, saveChanges: handleSaveChanges, rollback: handleRollback, reset }),
    [initialize, handleSaveChanges, handleRollback, reset],
  );

  return versioner;
}
